import { c as _c } from "react/compiler-runtime";
import { ArrowDownIcon, ArrowRightIcon, ChevronsUpDown, CircleIcon, CompassIcon, FilterIcon, LayoutGridIcon, NetworkIcon, SplineIcon } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePathname, useRouter } from '@/i18n/navigation';
import { cn } from '@/lib/utils';
import type { EdgeShape, EdgeStyleOptions, HierarchyDirection, LayoutMode, ViewMode } from '../_schemas/graph';
import { EDGE_SHAPES, HIERARCHY_DIRECTIONS, LAYOUT_MODES, PARAM_DIRECTION, PARAM_LAYOUT, PARAM_LETTER, PARAM_SHAPE, PARAM_VIEW, VIEW_MODES } from '../_schemas/graph';
import type { Letter } from '../_schemas/letter';
interface GraphFilterProps {
  dir: 'ltr' | 'rtl';
  viewMode: ViewMode;
  layoutMode: LayoutMode;
  hierarchyDirection: HierarchyDirection;
  edgeStyle: EdgeStyleOptions;
  letters: Letter[];
  selectedLetterId: number;
  onViewModeChange: (mode: ViewMode) => void;
  onLayoutModeChange: (mode: LayoutMode) => void;
  onHierarchyDirectionChange: (direction: HierarchyDirection) => void;
  onEdgeStyleChange: (style: Partial<EdgeStyleOptions>) => void;
  onLetterSelect: (id: number) => void;
}
export function GraphFilter(t0) {
  const $ = _c(74);
  const {
    dir,
    viewMode,
    layoutMode,
    hierarchyDirection,
    edgeStyle,
    letters,
    selectedLetterId,
    onViewModeChange,
    onLayoutModeChange,
    onHierarchyDirectionChange,
    onEdgeStyleChange,
    onLetterSelect
  } = t0;
  const t = useTranslations("playground_page.filter");
  const [isOpen, setIsOpen] = useState(true);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = (name, value) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(name, value);
      if (name === PARAM_VIEW) {
        if (value === "letter") {
          params.set(PARAM_LETTER, "1");
        } else {
          params.delete(PARAM_LETTER);
        }
      }
      if (name === PARAM_LAYOUT) {
        if (value === "hierarchical") {
          params.set(PARAM_DIRECTION, "vertical");
        } else {
          params.delete(PARAM_DIRECTION);
        }
      }
      return params.toString();
    };
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const updateQueryParams = t1;
  const t2 = isOpen ? "border-b" : "";
  let t3;
  if ($[2] !== t2) {
    t3 = cn("flex items-center justify-between gap-2 p-2 text-left font-semibold text-muted-foreground text-xs", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t("title");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ChevronsUpDown className="size-4" />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t4) {
    t6 = <CollapsibleTrigger className={t3}>{t4}{t5}</CollapsibleTrigger>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <FilterIcon className="size-4 shrink-0 text-muted-foreground" />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== onLetterSelect || $[12] !== onViewModeChange || $[13] !== pathname || $[14] !== router || $[15] !== t || $[16] !== updateQueryParams || $[17] !== viewMode) {
    t8 = VIEW_MODES.map(mode => <Button key={mode} variant="outline" size="sm" onClick={() => {
      onViewModeChange(mode);
      if (mode !== "letter") {
        onLetterSelect(1);
      }
      router.push(`${pathname}?${updateQueryParams(PARAM_VIEW, mode)}`);
    }} className={cn("flex-1 hover:bg-linear-to-r hover:from-amber-500 hover:to-amber-600 hover:text-white", viewMode === mode ? "bg-linear-to-r from-amber-500 to-amber-600 text-white" : "text-muted-foreground")}>{t(`view_mode.${mode}`)}</Button>);
    $[11] = onLetterSelect;
    $[12] = onViewModeChange;
    $[13] = pathname;
    $[14] = router;
    $[15] = t;
    $[16] = updateQueryParams;
    $[17] = viewMode;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== dir || $[20] !== t8) {
    t9 = <li id="tour-view-mode" className="flex items-center gap-2">{t7}<ButtonGroup dir={dir} className="flex-1">{t8}</ButtonGroup></li>;
    $[19] = dir;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <LayoutGridIcon className="size-4 text-muted-foreground" />;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== layoutMode || $[24] !== onHierarchyDirectionChange || $[25] !== onLayoutModeChange || $[26] !== pathname || $[27] !== router || $[28] !== t || $[29] !== updateQueryParams) {
    t11 = LAYOUT_MODES.map(mode_0 => <Button key={mode_0} variant="outline" size="sm" onClick={() => {
      onLayoutModeChange(mode_0);
      if (mode_0 !== "hierarchical") {
        onHierarchyDirectionChange("vertical");
      }
      router.push(`${pathname}?${updateQueryParams(PARAM_LAYOUT, mode_0)}`);
    }} className={cn("flex-1 hover:bg-linear-to-r hover:from-amber-500 hover:to-amber-600 hover:text-white", layoutMode === mode_0 ? "bg-linear-to-r from-amber-500 to-amber-600 text-white" : "text-muted-foreground")}>{mode_0 === "radial" ? <CircleIcon className="size-4" /> : <NetworkIcon className="size-4" />}{t(`layout_mode.${mode_0}`)}</Button>);
    $[23] = layoutMode;
    $[24] = onHierarchyDirectionChange;
    $[25] = onLayoutModeChange;
    $[26] = pathname;
    $[27] = router;
    $[28] = t;
    $[29] = updateQueryParams;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== dir || $[32] !== t11) {
    t12 = <li id="tour-layout-mode" className="flex items-center gap-2">{t10}<ButtonGroup dir={dir} className="flex-1">{t11}</ButtonGroup></li>;
    $[31] = dir;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== dir || $[35] !== hierarchyDirection || $[36] !== layoutMode || $[37] !== onHierarchyDirectionChange || $[38] !== pathname || $[39] !== router || $[40] !== t || $[41] !== updateQueryParams) {
    t13 = layoutMode === "hierarchical" && <li id="tour-hierarchy-direction" className="flex items-center gap-2"><CompassIcon className="size-4 text-muted-foreground" /><ButtonGroup dir={dir} className="flex-1">{HIERARCHY_DIRECTIONS.map(direction => <Button key={direction} variant="outline" size="sm" onClick={() => {
          onHierarchyDirectionChange(direction);
          router.push(`${pathname}?${updateQueryParams(PARAM_DIRECTION, direction)}`);
        }} className={cn("flex-1 hover:bg-linear-to-r hover:from-amber-500 hover:to-amber-600 hover:text-white", hierarchyDirection === direction ? "bg-linear-to-r from-amber-500 to-amber-600 text-white" : "text-muted-foreground")}>{direction === "vertical" ? <ArrowDownIcon className="size-4" /> : <ArrowRightIcon className="size-4" />}{t(`layout_mode.direction.${direction}`)}</Button>)}</ButtonGroup></li>;
    $[34] = dir;
    $[35] = hierarchyDirection;
    $[36] = layoutMode;
    $[37] = onHierarchyDirectionChange;
    $[38] = pathname;
    $[39] = router;
    $[40] = t;
    $[41] = updateQueryParams;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== dir || $[44] !== edgeStyle.isFloating || $[45] !== edgeStyle.shape || $[46] !== onEdgeStyleChange || $[47] !== pathname || $[48] !== router || $[49] !== t || $[50] !== updateQueryParams) {
    t14 = !edgeStyle.isFloating && <li id="tour-edge-shape" className="flex items-center gap-2"><SplineIcon className="size-4 text-muted-foreground" /><Select dir={dir} value={edgeStyle.shape} onValueChange={value_0 => {
        onEdgeStyleChange({
          shape: value_0 as EdgeShape
        });
        router.push(`${pathname}?${updateQueryParams(PARAM_SHAPE, value_0)}`);
      }}><SelectTrigger size="sm" className="flex-1 text-muted-foreground"><SelectValue /></SelectTrigger><SelectContent>{EDGE_SHAPES.map(shape => <SelectItem key={shape} value={shape}>{t(`edge.shape.${shape}`)}</SelectItem>)}</SelectContent></Select></li>;
    $[43] = dir;
    $[44] = edgeStyle.isFloating;
    $[45] = edgeStyle.shape;
    $[46] = onEdgeStyleChange;
    $[47] = pathname;
    $[48] = router;
    $[49] = t;
    $[50] = updateQueryParams;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] !== t12 || $[53] !== t13 || $[54] !== t14 || $[55] !== t9) {
    t15 = <ul className="flex flex-col gap-2">{t9}{t12}{t13}{t14}</ul>;
    $[52] = t12;
    $[53] = t13;
    $[54] = t14;
    $[55] = t9;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  let t16;
  if ($[57] !== dir || $[58] !== letters || $[59] !== onLetterSelect || $[60] !== pathname || $[61] !== router || $[62] !== selectedLetterId || $[63] !== updateQueryParams || $[64] !== viewMode) {
    t16 = viewMode === "letter" && <ScrollArea dir={dir} id="tour-letter-selector" className="w-full whitespace-nowrap rounded-lg border"><div className="flex w-max items-center gap-2 p-4">{letters.map(letter => <Button key={letter.id} variant="outline" size="icon-sm" onClick={() => {
          onLetterSelect(letter.id);
          router.push(`${pathname}?${updateQueryParams(PARAM_LETTER, letter.id.toString())}`);
        }} className={cn("rounded-full", selectedLetterId === letter.id ? "bg-linear-to-r from-amber-500 to-amber-600 text-white hover:text-white" : "text-muted-foreground")}>{letter.symbol}</Button>)}</div><ScrollBar orientation="horizontal" /></ScrollArea>;
    $[57] = dir;
    $[58] = letters;
    $[59] = onLetterSelect;
    $[60] = pathname;
    $[61] = router;
    $[62] = selectedLetterId;
    $[63] = updateQueryParams;
    $[64] = viewMode;
    $[65] = t16;
  } else {
    t16 = $[65];
  }
  let t17;
  if ($[66] !== t15 || $[67] !== t16) {
    t17 = <CollapsibleContent className="p-2 pt-0"><div className="flex flex-col gap-2">{t15}{t16}</div></CollapsibleContent>;
    $[66] = t15;
    $[67] = t16;
    $[68] = t17;
  } else {
    t17 = $[68];
  }
  let t18;
  if ($[69] !== dir || $[70] !== isOpen || $[71] !== t17 || $[72] !== t6) {
    t18 = <Collapsible id="tour-graph-filter" dir={dir} open={isOpen} onOpenChange={setIsOpen} className="flex w-full flex-col gap-2 rounded-lg border bg-background sm:w-96">{t6}{t17}</Collapsible>;
    $[69] = dir;
    $[70] = isOpen;
    $[71] = t17;
    $[72] = t6;
    $[73] = t18;
  } else {
    t18 = $[73];
  }
  return t18;
}